import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { AppointmentRequest } from '@app/models/appointmentRequest.model';
import { Site } from '@app/models/site.model';
import { SiteBookingSettings } from '@app/models/siteBookingSettings.model';
import { FeatureFlagService, FeatureKey } from '@app/services/feature-flag.service';
import { APPOINTMENT_REQUEST, CUSTOMER_ID, LocalStorageService, SITE, SITE_BOOKING_SETTINGS } from '@app/services/local-storage.service';
import { ContentService } from 'app-classic/services/content.service';
import { UserAccountService } from 'app-classic/services/user-account.service';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { LoginFormComponent } from '../user-accounts/login-form/login-form.component';

@Component({
    selector: 'app-title-bar',
    templateUrl: './title-bar.component.html',
    styleUrls: ['./title-bar.component.scss'],
    standalone: false,
})
export class TitleBarComponent implements OnInit, OnDestroy {
    public businessName: string = '';
    public customerID: string | undefined;
    public siteBookingSavedSubscription!: Subscription;
    public userAccountFeatureEnabled = false;
    public userLoggedIn: boolean = false;
    public appointmentRequest!: AppointmentRequest;
    public isRescheduleAppointment = false;
    private contentServiceSubscription: Subscription | undefined;
    public ngUnsubscribe: Subject<any> = new Subject();

    constructor(
        private localStorageService: LocalStorageService,
        private featureFlagService: FeatureFlagService,
        private userAccountService: UserAccountService,
        private bottomSheet: MatBottomSheet,
        private contentService: ContentService
    ) {}

    ngOnInit() {
        this.customerID = this.localStorageService.getString(CUSTOMER_ID);
        const siteBookingSettings = this.localStorageService.get(SITE_BOOKING_SETTINGS) as SiteBookingSettings;
        this.updateFromSiteBookingSettings(siteBookingSettings);

        this.siteBookingSavedSubscription = this.localStorageService.localStorageSiteBookingSettings$.subscribe((siteBookingSettings) => {
            this.updateFromSiteBookingSettings(siteBookingSettings ?? undefined);
        });
        this.userAccountService.userAccountUpdated$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((userAccount) => {
            this.userLoggedIn = userAccount !== undefined;
        });
        this.initialize();
        this.contentServiceSubscription = this.contentService.dataUpdated$.subscribe((data) => {
            if (data === 'new appointment') {
                this.initialize();
            }
        });
    }

    ngOnDestroy(): void {
        this.siteBookingSavedSubscription.unsubscribe();
        this.ngUnsubscribe.complete();
        this.contentServiceSubscription?.unsubscribe();
    }

    public onClickLogIn(): void {
        this.bottomSheet.open(LoginFormComponent, { disableClose: true });
    }

    public onClickAccount(): void {
        const site = this.localStorageService.get(SITE) as Site;
        let url = site?.bookingURL + '/User#overview';
        if (!url.startsWith('http')) {
            url = 'https://' + url;
        }
        if (this.isEmbedded()) {
            window.open(url, '_blank', 'noopener,noreferrer');
        } else {
            window.location.href = url;
        }
    }

    private isEmbedded(): boolean {
        try {
            return window.self !== window.top;
        } catch {
            return true;
        }
    }

    private initialize(): void {
        this.appointmentRequest = this.localStorageService.get(APPOINTMENT_REQUEST) as AppointmentRequest;
        this.isRescheduleAppointment = !!this.appointmentRequest?.rescheduleGuid;
    }

    private updateFromSiteBookingSettings(siteBookingSettings?: SiteBookingSettings): void {
        this.businessName = siteBookingSettings?.businessName ?? 'Business Name';
        this.userAccountFeatureEnabled =
            this.featureFlagService.getFeatureFlag(FeatureKey.UserAccounts) && siteBookingSettings?.allowClientOnlineAccountAccess === true;
        this.userLoggedIn = this.userAccountFeatureEnabled && this.userAccountService.getCurrentUser() !== undefined;
    }
}
